import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronLeft, ShoppingCart, Share2, Tag, Instagram } from 'lucide-react';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton, FacebookIcon, TwitterIcon, WhatsappIcon } from 'react-share';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error carregant producte:', error);
        toast.error('Error carregant el producte');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success('Producte afegit a la cistella');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center">
        <p className="text-muted-foreground">Carregant producte...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-muted-foreground mb-6">Producte no trobat</p>
          <Link to="/catalog">
            <Button>Tornar al catàleg</Button>
          </Link>
        </div>
      </div>
    );
  }

  const finalPrice = product.discount > 0
    ? product.price * (1 - product.discount / 100)
    : product.price;

  const shareUrl = window.location.href;
  const shareTitle = `${product.name} - Estel Regals`;

  return (
    <div data-testid="product-detail-page" className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <Link 
          to="/catalog" 
          data-testid="back-to-catalog"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ChevronLeft className="h-4 w-4" />
          Tornar al catàleg
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative aspect-[3/4] rounded-3xl overflow-hidden bg-muted">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-contain"
              />
              {product.discount > 0 && (
                <div className="absolute top-6 right-6 bg-accent text-white px-4 py-2 rounded-full flex items-center gap-2 text-base font-medium shadow-lg">
                  <Tag className="h-4 w-4" />
                  -{product.discount}%
                </div>
              )}
            </div>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="text-sm tracking-widest uppercase text-muted-foreground mb-4">
              {product.category}
            </div>

            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-6">
              {product.name}
            </h1>

            <div className="flex items-baseline gap-4 mb-8">
              {product.discount > 0 ? (
                <>
                  <span className="text-3xl font-semibold text-foreground">
                    {finalPrice.toFixed(2).replace('.', ',')} €
                  </span>
                  <span className="text-xl text-muted-foreground line-through">
                    {product.price.toFixed(2).replace('.', ',')} €
                  </span>
                </>
              ) : (
                <span className="text-3xl font-semibold text-foreground">
                  {product.price.toFixed(2).replace('.', ',')} €
                </span>
              )}
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-foreground mb-3">
                Quantitat
              </label>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="decrease-quantity"
                  className="h-12 w-12 rounded-xl"
                >
                  -
                </Button>
                <span data-testid="product-quantity" className="text-lg font-medium min-w-[3rem] text-center">
                  {quantity}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="increase-quantity"
                  className="h-12 w-12 rounded-xl"
                >
                  +
                </Button>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              onClick={handleAddToCart}
              data-testid="add-to-cart-button"
              className="w-full rounded-xl h-14 text-base font-medium mb-6"
              size="lg"
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              Afegir a la cistella
            </Button>

            {/* Share */}
            <div className="border-t border-border pt-6">
              <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                <Share2 className="h-4 w-4" />
                Compartir
              </h3>
              <div className="flex items-center gap-3">
                <FacebookShareButton url={shareUrl} quote={shareTitle} data-testid="share-facebook">
                  <FacebookIcon size={40} round />
                </FacebookShareButton>
                <TwitterShareButton url={shareUrl} title={shareTitle} data-testid="share-twitter">
                  <TwitterIcon size={40} round />
                </TwitterShareButton>
                <WhatsappShareButton url={shareUrl} title={shareTitle} data-testid="share-whatsapp">
                  <WhatsappIcon size={40} round />
                </WhatsappShareButton>
                <button
                  onClick={() => {
                    const text = `${shareTitle} - ${shareUrl}`;
                    if (navigator.share) {
                      navigator.share({ title: shareTitle, text: text, url: shareUrl });
                    } else {
                      navigator.clipboard.writeText(text);
                      toast.success('Enllaç copiat! Pots compartir-lo a Instagram');
                    }
                  }}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 hover:opacity-80 transition-opacity"
                  title="Compartir a Instagram"
                  data-testid="share-instagram"
                >
                  <Instagram className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;